import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ClipboardList, Search, FileDown, Printer, Ban, Plus, Pill,
  AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../theme';
import { SectionCard, StatCard, StatusPill } from '../components/swipe';

// ═══════════════════════════════════════════════════════════════════
//  Dispensing Register — the legal register UI for medicines handed
//  to patients. Reads from /api/dispensing-records.
//
//  Compliance rules baked into this page:
//    - Records cannot be edited after creation (only voided with reason)
//    - Hard-delete is not exposed
//    - Voided rows shown in red, never hidden — auditor must see them
//    - Schedule-H/H1/X rows surface a "Print Label" pill if pending
//    - Dispense form requires patient + prescriber DCI Regn No
// ═══════════════════════════════════════════════════════════════════

const SCHEDULE_TONES = {
  OTC: 'neutral', G: 'neutral', NA: 'neutral',
  H:   'warning',
  H1:  'danger',
  X:   'danger',
};

const fmtDate     = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export default function DispensingRegisterPage({ API_BASE, authHeaders }) {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from: '', to: '', schedule: '', search: '', voided: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from)     params.set('from', filters.from);
      if (filters.to)       params.set('to',   filters.to);
      if (filters.schedule) params.set('schedule', filters.schedule);
      if (filters.search)   params.set('search',   filters.search);
      if (filters.voided)   params.set('voided',   filters.voided);
      const [l, s] = await Promise.all([
        axios.get(`${API_BASE}/dispensing-records?${params.toString()}`),
        axios.get(`${API_BASE}/dispensing-records/summary`),
      ]);
      setList(l.data || []);
      setSummary(s.data);
    } catch (e) { setMsg(e.response?.data?.error || e.message); }
    setLoading(false);
  }, [API_BASE, filters]);

  useEffect(() => { load(); }, [load]);

  const downloadRegister = () => {
    const params = new URLSearchParams();
    if (filters.from)     params.set('from', filters.from);
    if (filters.to)       params.set('to',   filters.to);
    if (filters.schedule) params.set('schedule', filters.schedule);
    window.open(`${API_BASE}/dispensing-records/register/pdf?${params.toString()}`, '_blank');
  };

  const printLabel = async (id) => {
    try {
      window.open(`${API_BASE}/dispensing-records/label/${id}/pdf`, '_blank');
      await axios.post(`${API_BASE}/dispensing-records/${id}/print-label`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const labelPending = (r) => r.labelRequired && !r.labelPrinted && !r.voided;

  return (
    <div style={{ padding: '20px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <SectionCard variant="white" bordered>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ClipboardList size={28} color={theme.primary} />
            <div>
              <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.15rem', fontWeight: 800 }}>Dispensing Register (Legal)</h2>
              <p style={{ margin: '2px 0 0', color: theme.textMuted, fontSize: '0.78rem' }}>
                Drugs &amp; Cosmetics Rules 1945 · Rule 123 + Schedule K, Cl. 5 — immutable record of every medicine handed to a patient.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowAdd(true)} style={btnPrimary}>
              <Plus size={16} /> New Dispense
            </button>
            <button onClick={downloadRegister} style={btnOutline}>
              <FileDown size={16} /> Export PDF
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Stats */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10, marginBottom: 14 }}>
          <StatCard title="Total entries" value={summary.total} sub={`Since ${summary.oldestEntryDate ? new Date(summary.oldestEntryDate).getFullYear() : '—'}`} />
          <StatCard title="Last 30 days" value={summary.last30} sub={`Last 12mo: ${summary.lastYear}`} />
          <StatCard title="Schedule H/H1/X (30d)" value={(summary.bySchedule.H||0) + (summary.bySchedule.H1||0) + (summary.bySchedule.X||0)} accent={theme.warning} />
          <StatCard title="Labels pending (30d)" value={summary.labelPendingLast30} accent={summary.labelPendingLast30 > 0 ? theme.danger : theme.success}
            sub={summary.labelPendingLast30 > 0 ? 'Print before close' : 'All labels printed'} />
          <StatCard title="Voided entries" value={summary.voided} accent={theme.textMuted} sub={`Retention target: ${summary.retentionTargetYrs} yrs`} />
        </div>
      )}

      {/* Filters */}
      <SectionCard variant="blue" bordered title="Filter & search">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Schedule</label>
            <select value={filters.schedule} onChange={e => setFilters(f => ({ ...f, schedule: e.target.value }))} style={inputStyle}>
              <option value="">All</option>
              <option value="OTC">OTC</option><option value="G">G</option>
              <option value="H">H</option><option value="H1">H1</option><option value="X">X</option>
              <option value="NA">NA</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Show voided</label>
            <select value={filters.voided} onChange={e => setFilters(f => ({ ...f, voided: e.target.value }))} style={inputStyle}>
              <option value="">All</option>
              <option value="false">Active only</option>
              <option value="true">Voided only</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Search (entry / UHID / patient / medicine / batch)</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} color={theme.textMuted} style={{ position: 'absolute', left: 10, top: 11 }} />
              <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Type to search…" style={{ ...inputStyle, paddingLeft: 30 }} />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Table */}
      <SectionCard variant="white" bordered title={`Register (${list.length})`}
        right={msg ? <span style={{ color: theme.danger, fontSize: '0.78rem' }}>{msg}</span> : null}>
        {loading ? (
          <p style={{ color: theme.textMuted, fontSize: '0.85rem' }}>Loading…</p>
        ) : list.length === 0 ? (
          <p style={{ color: theme.textMuted, fontSize: '0.85rem' }}>
            No dispensing entries match these filters. Click <strong>New Dispense</strong> to add one.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>Entry No</th>
                  <th style={th}>Date</th>
                  <th style={th}>Patient</th>
                  <th style={th}>Medicine</th>
                  <th style={th}>Sch</th>
                  <th style={th}>Batch · Exp</th>
                  <th style={th}>Qty</th>
                  <th style={th}>DCI Regn</th>
                  <th style={th}>Dispensed By</th>
                  <th style={th}>State</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {list.map(r => (
                  <tr key={r._id} style={{ background: r.voided ? '#fef2f2' : 'transparent' }}>
                    <td style={tdMono}>{r.entryNo}</td>
                    <td style={td}>{fmtDateTime(r.dispensedAt)}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.patientName}</div>
                      <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>UHID {r.patientUHID || '—'}</div>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.medicineName}{r.strength ? ` ${r.strength}` : ''}</div>
                      <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{r.form || ''}</div>
                    </td>
                    <td style={td}><StatusPill tone={SCHEDULE_TONES[r.schedule] || 'neutral'}>{r.schedule}</StatusPill></td>
                    <td style={td}>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.batchNo}</div>
                      <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>Exp {fmtDate(r.expiryDate)}</div>
                    </td>
                    <td style={tdRight}>{r.quantityDispensed} {r.unit || ''}</td>
                    <td style={tdMono}>{r.prescriberRegnNo}</td>
                    <td style={td}>{r.dispenserName}</td>
                    <td style={td}>
                      {r.voided ? <StatusPill tone="danger">VOID</StatusPill>
                        : labelPending(r) ? <StatusPill tone="warning">Label pending</StatusPill>
                        : <StatusPill tone="success">OK</StatusPill>}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {!r.voided && labelPending(r) && (
                          <button onClick={() => printLabel(r._id)} style={btnTiny} title="Print Schedule-H label">
                            <Printer size={12} /> Label
                          </button>
                        )}
                        {!r.voided && (
                          <button onClick={() => setVoiding(r)} style={{ ...btnTiny, color: theme.danger, borderColor: theme.danger }} title="Void with reason">
                            <Ban size={12} /> Void
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Audit footer */}
      <div style={{ marginTop: 14, padding: 12, background: '#fefce8', border: `1px solid #fde68a`, borderRadius: 10, fontSize: '0.78rem', color: '#78350f' }}>
        <ShieldCheck size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        <strong>Audit notice:</strong> This register is open to inspection by Drugs Inspector / FDA on demand.
        Records cannot be edited or hard-deleted. Voiding requires a written reason and reverses the inventory deduction.
      </div>

      {showAdd && (
        <DispenseFormModal
          API_BASE={API_BASE}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {voiding && (
        <VoidModal
          record={voiding}
          API_BASE={API_BASE}
          onClose={() => setVoiding(null)}
          onSaved={() => { setVoiding(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Add dispense form ─────────────────────────────────────────────
function DispenseFormModal({ API_BASE, onClose, onSaved }) {
  const [patients, setPatients]   = useState([]);
  const [meds, setMeds]           = useState([]);
  const [doctors, setDoctors]     = useState([]);
  const [form, setForm] = useState({
    patientId: '', medicineId: '', prescriberId: '',
    quantityDispensed: 1, dose: '', route: 'PO', durationDays: 0,
    prescriberRegnNo: '', dispenserName: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/patients?limit=500`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/pharmacy/items?inStock=true&limit=500`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/doctors?limit=200`).catch(() => ({ data: [] })),
    ]).then(([p, m, d]) => {
      setPatients(Array.isArray(p.data) ? p.data : (p.data?.items || p.data?.list || []));
      setMeds(Array.isArray(m.data) ? m.data : (m.data?.items || m.data?.list || []));
      setDoctors(Array.isArray(d.data) ? d.data : (d.data?.items || d.data?.list || []));
    });
  }, [API_BASE]);

  const onPickDoctor = (id) => {
    const d = doctors.find(x => x._id === id);
    setForm(f => ({ ...f, prescriberId: id, prescriberRegnNo: d?.registrationNumber || f.prescriberRegnNo }));
  };

  const submit = async () => {
    setErr('');
    if (!form.patientId)     return setErr('Patient is required');
    if (!form.medicineId)    return setErr('Medicine is required');
    if (!form.quantityDispensed || form.quantityDispensed <= 0) return setErr('Quantity must be > 0');
    if (!form.prescriberRegnNo) return setErr('Prescriber DCI registration number is required');
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/dispensing-records`, form);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  const selectedMed = meds.find(m => m._id === form.medicineId);

  return (
    <Modal title="New Dispense Entry" onClose={onClose}>
      {err && (
        <div style={{ padding: 10, background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: '0.82rem', marginBottom: 12 }}>
          <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {err}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Patient *</label>
          <select value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))} style={inputStyle}>
            <option value="">— Select patient —</option>
            {patients.map(p => (
              <option key={p._id} value={p._id}>
                {p.uhid ? `${p.uhid} · ` : ''}{p.name}{p.phone ? ` · ${p.phone}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Medicine *</label>
          <select value={form.medicineId} onChange={e => setForm(f => ({ ...f, medicineId: e.target.value }))} style={inputStyle}>
            <option value="">— Select medicine —</option>
            {meds.map(m => (
              <option key={m._id} value={m._id}>
                {m.name} {m.schedule ? `[Sch ${m.schedule}]` : ''} {m.currentStock != null ? `· stock ${m.currentStock}` : ''}
              </option>
            ))}
          </select>
          {selectedMed && ['H', 'H1', 'X'].includes((selectedMed.schedule || '').toUpperCase()) && (
            <div style={{ marginTop: 6, padding: 6, background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: '0.72rem' }}>
              <Pill size={11} style={{ verticalAlign: 'middle' }} /> Schedule {selectedMed.schedule.toUpperCase()} — label will be required after dispense.
            </div>
          )}
        </div>
        <div>
          <label style={labelStyle}>Quantity *</label>
          <input type="number" min={1} value={form.quantityDispensed}
            onChange={e => setForm(f => ({ ...f, quantityDispensed: Number(e.target.value) }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Dose</label>
          <input value={form.dose} onChange={e => setForm(f => ({ ...f, dose: e.target.value }))}
            placeholder="e.g. 1 tab TDS x 5d" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Route</label>
          <select value={form.route} onChange={e => setForm(f => ({ ...f, route: e.target.value }))} style={inputStyle}>
            <option value="PO">PO (Oral)</option><option value="IM">IM</option>
            <option value="IV">IV</option><option value="topical">Topical</option>
            <option value="MW">Mouthwash</option><option value="other">Other</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Duration (days)</label>
          <input type="number" min={0} value={form.durationDays}
            onChange={e => setForm(f => ({ ...f, durationDays: Number(e.target.value) }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Prescriber</label>
          <select value={form.prescriberId} onChange={e => onPickDoctor(e.target.value)} style={inputStyle}>
            <option value="">— Select doctor (auto-fills DCI no) —</option>
            {doctors.map(d => (
              <option key={d._id} value={d._id}>
                {d.name}{d.registrationNumber ? ` · ${d.registrationNumber}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>DCI Registration No *</label>
          <input value={form.prescriberRegnNo} onChange={e => setForm(f => ({ ...f, prescriberRegnNo: e.target.value }))}
            placeholder="A/12345" style={inputStyle} />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={labelStyle}>Dispenser name (override — defaults to logged-in user)</label>
          <input value={form.dispenserName} onChange={e => setForm(f => ({ ...f, dispenserName: e.target.value }))}
            placeholder="Auto-filled from your account" style={inputStyle} />
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} style={btnOutline}>Cancel</button>
        <button onClick={submit} disabled={busy} style={btnPrimary}>
          {busy ? 'Saving…' : 'Save & generate Entry No'}
        </button>
      </div>
    </Modal>
  );
}

// ── Void modal ────────────────────────────────────────────────────
function VoidModal({ record, API_BASE, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const submit = async () => {
    setErr('');
    if (reason.trim().length < 6) return setErr('Reason must be at least 6 characters (auditable)');
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/dispensing-records/${record._id}/void`, { reason: reason.trim() });
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  return (
    <Modal title={`Void Entry ${record.entryNo}`} onClose={onClose}>
      <p style={{ fontSize: '0.85rem', color: theme.textMuted, marginTop: 0 }}>
        Voiding marks this record as cancelled in the legal register and reverses
        the inventory deduction for batch <strong>{record.batchNo}</strong> ({record.quantityDispensed} units).
        The record itself stays visible to the auditor with your reason.
      </p>
      <label style={labelStyle}>Reason (required, auditable)</label>
      <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
        placeholder="e.g. Wrong medicine selected · Patient never collected · Data entry error"
        style={{ ...inputStyle, minHeight: 80 }} />
      {err && <div style={{ marginTop: 8, color: theme.danger, fontSize: '0.8rem' }}>{err}</div>}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} style={btnOutline}>Cancel</button>
        <button onClick={submit} disabled={busy} style={{ ...btnPrimary, background: theme.danger }}>
          {busy ? 'Voiding…' : 'Void entry'}
        </button>
      </div>
    </Modal>
  );
}

// ── Modal shell ───────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 14, padding: 20, width: '100%', maxWidth: 720,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: theme.navy, fontSize: '1rem', fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: theme.textMuted }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Inline styles ─────────────────────────────────────────────────
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' };
const th = { textAlign: 'left', padding: '8px 10px', background: theme.bg, color: theme.navy, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${theme.border}` };
const td = { padding: '8px 10px', borderBottom: `1px solid ${theme.border}`, color: theme.navy, verticalAlign: 'top' };
const tdMono = { ...td, fontFamily: 'monospace', fontSize: '0.78rem' };
const tdRight = { ...td, textAlign: 'right' };
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: theme.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnOutline = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'white', color: theme.navy, border: `1px solid ${theme.border}`, borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnTiny    = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'white', color: theme.navy, border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' };
